import { Link } from 'react-router-dom';
import {
    formatCpmCount,
    formatCpmPercent,
} from '../../../constants/centralProductMaster.constants';
import type { CpmStatsResponse } from '../../../types/centralProductMaster.types';
import SectionError from './SectionError';

interface TenantLinkagePanelProps {
    tenantLinkage: CpmStatsResponse['tenantLinkage'];
    linkNeedsReview: number;
    infoOpen: boolean;
    onToggleInfo: () => void;
    error?: boolean;
    onRetry?: () => void;
    retrying?: boolean;
}

function pctColor(linked: number, total: number): string {
    if (total <= 0) return 'var(--cpm-text-secondary)';
    const p = (linked / total) * 100;
    if (p >= 92) return 'var(--cpm-success-text)';
    return 'var(--cpm-warning-text-med)';
}

function barColor(linked: number, total: number): string {
    if (total <= 0) return 'var(--cpm-text-tertiary)';
    const p = (linked / total) * 100;
    if (p >= 92) return 'var(--cpm-success-dot)';
    return 'var(--cpm-warning-dot)';
}

export default function TenantLinkagePanel({
    tenantLinkage,
    linkNeedsReview,
    infoOpen,
    onToggleInfo,
    error = false,
    onRetry,
    retrying = false,
}: TenantLinkagePanelProps) {
    const rows = [
        {
            title: 'Brands',
            code: 'ProductBrand',
            bucket: tenantLinkage.brands,
        },
        {
            title: 'Products',
            code: 'StoreBrandProduct',
            bucket: tenantLinkage.products,
        },
        {
            title: 'Variants',
            code: 'StoreBrandVariant',
            bucket: tenantLinkage.variants,
        },
    ];

    const unlinkedTotal =
        tenantLinkage.brands.unlinked +
        tenantLinkage.products.unlinked +
        tenantLinkage.variants.unlinked;

    return (
        <div
            className="bg-[var(--cpm-surface-card)] border border-[var(--cpm-border-default)] rounded-[16px] shadow-[var(--cpm-shadow-card)] flex flex-col"
            style={{ padding: 20, gap: 18 }}
        >
            <div className="flex flex-col" style={{ gap: 4 }}>
                <div className="flex items-center" style={{ gap: 8 }}>
                    <h2 className="m-0 !text-[14px] !font-extrabold !text-[var(--cpm-text-primary)]">
                        Tenant linkage health
                    </h2>
                    <button
                        type="button"
                        title="What counts here"
                        onClick={onToggleInfo}
                        className="inline-flex items-center justify-center rounded-full bg-[var(--cpm-purple-tint-13)] text-[var(--cpm-brand-purple)] border-0 cursor-pointer"
                        style={{ width: 20, height: 20, padding: 0 }}
                    >
                        <svg
                            width="13"
                            height="13"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2.2"
                        >
                            <circle cx="12" cy="12" r="10" />
                            <path d="M12 16v-4" />
                            <path d="M12 8h.01" />
                        </svg>
                    </button>
                    {!error ? (
                        <span
                            className="ml-auto uppercase text-[var(--cpm-text-tertiary)]"
                            style={{ fontSize: 11, fontWeight: 800, letterSpacing: '0.04em' }}
                        >
                            Linked to master
                        </span>
                    ) : null}
                </div>
                <span
                    className="text-[var(--cpm-text-secondary)]"
                    style={{ fontSize: 12.5, fontWeight: 700 }}
                >
                    Alcoholic products · live &amp; trial tenants
                </span>
                {infoOpen ? (
                    <span
                        className="bg-[var(--cpm-info-bg)] border border-[var(--cpm-info-border)] text-[var(--cpm-info-text)] rounded-[8px]"
                        style={{
                            marginTop: 4,
                            padding: '11px 13px',
                            fontSize: 12.5,
                            fontWeight: 600,
                            lineHeight: 1.45,
                        }}
                    >
                        Only records the linking pipeline works on are counted: alcoholic products
                        at live and trial tenants. Non-alcoholic products and inactive or churned
                        tenants are permanently out of scope and never count against these
                        percentages.
                    </span>
                ) : null}
            </div>

            {error ? (
                <SectionError
                    title="Couldn't load tenant linkage"
                    body="Live and trial tenant linkage counts did not return. Nothing has changed — retry to load the breakdown."
                    onRetry={onRetry}
                    retrying={retrying}
                />
            ) : (
            <>
            <div
                className="flex flex-col flex-1 justify-evenly"
                style={{ gap: 18 }}
            >
                {rows.map((row) => {
                    const { linked, unlinked, total } = row.bucket;
                    const pct = total > 0 ? (linked / total) * 100 : 0;
                    return (
                        <div key={row.title} className="flex flex-col" style={{ gap: 8 }}>
                            <div className="flex items-baseline" style={{ gap: 8 }}>
                                <span style={{ fontSize: 13.5, fontWeight: 800 }}>{row.title}</span>
                                <span
                                    className="font-[family-name:var(--cpm-font-mono)] text-[var(--cpm-text-tertiary)]"
                                    style={{
                                        fontSize: 10,
                                        fontWeight: 700,
                                        letterSpacing: '0.04em',
                                    }}
                                >
                                    {row.code}
                                </span>
                                <span
                                    className="ml-auto tabular"
                                    style={{
                                        fontSize: 18,
                                        fontWeight: 800,
                                        letterSpacing: '-0.01em',
                                        color: pctColor(linked, total),
                                    }}
                                >
                                    {formatCpmPercent(linked, total)}
                                </span>
                            </div>
                            <div
                                className="rounded-full overflow-hidden bg-[var(--cpm-surface-skeleton)]"
                                style={{ height: 10 }}
                            >
                                <span
                                    style={{
                                        display: 'block',
                                        width: `${pct}%`,
                                        height: '100%',
                                        background: barColor(linked, total),
                                    }}
                                />
                            </div>
                            <div
                                className="flex tabular text-[var(--cpm-text-secondary)]"
                                style={{ gap: 16, fontSize: 12, fontWeight: 600 }}
                            >
                                <span>
                                    {formatCpmCount(linked)} / {formatCpmCount(total)} linked
                                </span>
                                <span className="ml-auto">
                                    {formatCpmCount(unlinked)} unlinked
                                </span>
                            </div>
                        </div>
                    );
                })}
            </div>

            <div
                className="mt-auto border-t border-[var(--cpm-border-default)] flex flex-col"
                style={{ paddingTop: 14, gap: 12 }}
            >
                <span
                    className="text-[var(--cpm-text-secondary)]"
                    style={{ fontSize: 12.5, fontWeight: 600, lineHeight: 1.45 }}
                >
                    {formatCpmCount(unlinkedTotal)} in-scope tenant records remain unlinked. Most
                    clear automatically on the next daily linking run; the rest need a human
                    decision.
                </span>
                <Link
                    to="/central-product-master/verification-queue?tab=link"
                    className="flex items-center rounded-[10px] no-underline !text-inherit bg-[var(--cpm-purple-tint-3)] border-[1.5px] border-[var(--cpm-brand-purple)] hover:bg-[var(--cpm-purple-tint-13)]"
                    style={{ gap: 12, padding: '14px 15px' }}
                >
                    <span
                        className="shrink-0 flex items-center justify-center bg-[var(--cpm-purple-tint-13)] text-[var(--cpm-brand-purple)]"
                        style={{ width: 30, height: 30, borderRadius: 8 }}
                    >
                        <svg
                            width="15"
                            height="15"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                        >
                            <path d="M9 17H7A5 5 0 0 1 7 7h2" />
                            <path d="M15 7h2a5 5 0 0 1 0 10h-2" />
                            <path d="M8 12h8" />
                        </svg>
                    </span>
                    <span className="flex flex-col min-w-0" style={{ gap: 2 }}>
                        <span
                            className="text-[var(--cpm-brand-navy)]"
                            style={{ fontSize: 13.5, fontWeight: 800 }}
                        >
                            Review {formatCpmCount(linkNeedsReview)} link decisions
                        </span>
                        <span
                            className="text-[var(--cpm-text-secondary)]"
                            style={{ fontSize: 12, fontWeight: 600 }}
                        >
                            Opens the Verification Queue on Link Review
                        </span>
                    </span>
                    <svg
                        width="16"
                        height="16"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="var(--cpm-brand-purple)"
                        strokeWidth="2"
                        className="ml-auto shrink-0"
                    >
                        <path d="M5 12h14" />
                        <path d="m12 5 7 7-7 7" />
                    </svg>
                </Link>
            </div>
            </>
            )}
        </div>
    );
}
